// ParagraphContainer.jsx - Refactored to handle its own excerpt data and selection
import React, { useCallback, memo } from 'react';
import SpeakerParagraphComponent from './SpeakerParagraphComponent';
import { useCodedParagraph } from 'hooks/useCodedParagraph';
import { useParagraphExcerpts } from 'hooks/excerpts/queries';
import { useExcerpt } from 'hooks/excerpts/queries';
import { useActiveCode } from 'contexts/ActiveCodeContext';
import { useEditParagraph } from 'contexts/EditParagraphContext';
import ComparisonUrlUtil from 'utils/ComparisonUrlUtil';
import { filterCodingsToUser } from './codeDiffUtils';

const ParagraphContainer = memo(({
  paragraph,
  speakerColorHash,
  selection, // New prop: { selectedExcerptId, selectionStart, selectionEnd } or null
  transcriptID,
  codeableType,
  absoluteIndex,
  paragraphIndex,
  disableEditing = false,
  hasEditPermission,
  onParagraphClick,
  canEdit,
  isPrint,
  marginLeft,
  marginRight,
  title,
  color,
  logo,
  hideTitle,
  onTitleClick,
  filterUserID = null, // New prop for filtering excerpts by user
  showCodeSelector = false, // Show CodeMultiSelect in ParagraphCodeList (comparison page)
  enableHover = false,
  // Comparison page only: absolute offsets (the diff-segment edges — the
  // union of both coders' excerpt boundaries) at which rendered spans split
  // in addition to this column's own excerpt boundaries, so each span maps
  // 1:1 to one agreement classification. Null everywhere else (no-op).
  extraBoundaries = null
}) => {
  // Get excerpts specific to this paragraph
  const { excerpts: rawExcerpts } = useParagraphExcerpts(transcriptID, codeableType, paragraph.start, paragraph.end);

  // Filter excerpts by user ID with memoization. Shares codeDiffUtils'
  // filterCodingsToUser: the agreement overlay must classify from exactly
  // the excerpt set this column renders, so the two must never drift.
  const filteredExcerpts = React.useMemo(() => {
    if (!rawExcerpts || filterUserID === null) {
      return rawExcerpts || [];
    }

    // Parse the filter user ID (handles both numeric IDs and special strings like 'bot')
    return filterCodingsToUser(rawExcerpts, ComparisonUrlUtil.parseUserId(filterUserID));
  }, [rawExcerpts, filterUserID]);

  // Get selected excerpt data if we have a selectedExcerptId
  const { data: selectedExcerpt } = useExcerpt(selection?.selectedExcerptId);

  const { activeCodeId, setActiveCodeId } = useActiveCode();

     
  // Local handlers for prime code management
  const handleSetPrimeCode = useCallback((codeId) => {
    setActiveCodeId(codeId);
  }, []);
     
  const handleRemovePrimeCode = useCallback(() => {
    setActiveCodeId(null);
  }, []);

  // Create effective select object for this paragraph
  const effectiveSelect = React.useMemo(() => {
    if (!selection) return null;

    // If we have a selected excerpt and it belongs to this paragraph, use it
    if (selectedExcerpt &&
        selectedExcerpt.start >= paragraph.start &&
        selectedExcerpt.end <= paragraph.end) {
      return selectedExcerpt;
    }

    // Otherwise, if we have selection boundaries, create a select object
    if (selection.selectionStart !== undefined && selection.selectionEnd !== undefined) {
      return {
        start: selection.selectionStart,
        end: selection.selectionEnd
      };
    }

    return null;
  }, [selection, selectedExcerpt, paragraph.start, paragraph.end]);

  // Compute the coded paragraph data locally with memoization
  const codedParagraph = useCodedParagraph(
    paragraph,
    filteredExcerpts,
    speakerColorHash,
    activeCodeId,
    effectiveSelect,
    selection?.selectedExcerptId,
    extraBoundaries
  );
 
  const { startEdit } = useEditParagraph();

  const onStartEditing = useCallback((e) => {
    // Skip if editing is disabled
    if (disableEditing) return;
    if (!startEdit) return;
         
    startEdit(absoluteIndex);
    if (e.stopPropagation) e.stopPropagation();
  }, [absoluteIndex, disableEditing, startEdit]);
 
  // Don't render if no coded paragraph or empty range
  if (!codedParagraph || codedParagraph.start === codedParagraph.end) {
    return null;
  }
 
  return (
    <SpeakerParagraphComponent
      codedParagraph={codedParagraph}
      paragraphIndex={paragraphIndex}
      hasEditPermission={hasEditPermission}
      onParagraphClick={onParagraphClick}
      onEditClick={disableEditing ? null : onStartEditing}
      canEdit={canEdit}
      isPrint={isPrint}
      marginLeft={marginLeft}
      marginRight={marginRight}
      title={title}
      color={color}
      logo={logo}
      hideTitle={hideTitle}
      onTitleClick={onTitleClick}
      setPrimeCode={handleSetPrimeCode}
      removePrimeCode={handleRemovePrimeCode}
      transcriptID={transcriptID}
      primeCode={activeCodeId}
      filteredExcerpts={filteredExcerpts}
      showCodeSelector={showCodeSelector}
      selectedExcerptId={selection?.selectedExcerptId}
      hasTextSelection={!!effectiveSelect && (
        !selection?.selectedExcerptId ||
        !filteredExcerpts.some(e => e.id?.toString() === selection?.selectedExcerptId?.toString())
      )}
      enableHover={enableHover}
    />
  );
});

export default ParagraphContainer;